import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SupportedLanguage } from '@/lib/i18n/languages';
import { LANGUAGE_PILL_TRIGGER_CLASS } from './languagePillStyles';

type Language = SupportedLanguage;

interface LanguagePillMenuProps {
  languages: Language[];
  currentLanguage: Language;
  shortCode: string;
  /** Class of the trigger button (differs between the pill in the app chrome and the marketing nav). */
  triggerClassName?: string;
  /** Open straight away: the user already clicked the placeholder while this chunk was loading. */
  defaultOpen?: boolean;
}

/** The interactive dropdown of LanguagePill (split out so Radix menu code loads lazily). */
export default function LanguagePillMenu({
  languages,
  currentLanguage,
  shortCode,
  triggerClassName = LANGUAGE_PILL_TRIGGER_CLASS,
  defaultOpen,
}: LanguagePillMenuProps) {
  const { i18n } = useTranslation('common');

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('cleancal_language', languageCode);
  };

  return (
    <DropdownMenu defaultOpen={defaultOpen}>
      <DropdownMenuTrigger className={triggerClassName}>
        <span className="text-sm leading-none">{currentLanguage.flag}</span>
        <span className="hidden sm:inline tabular-nums">{shortCode}</span>
        <span className="sr-only">Select language</span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        {languages.map((language) => {
          const active = language.code === i18n.language;
          return (
            <DropdownMenuItem
              key={language.code}
              onSelect={() => handleLanguageChange(language.code)}
              className={cn('gap-2 text-sm', active && 'font-medium')}
            >
              <span className="text-base leading-none">{language.flag}</span>
              <span className="flex-1">{language.name}</span>
              {active && <Check className="h-3.5 w-3.5 opacity-70" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
